/**
 * Catalog entries admin (ADMIN-PR2-b, Model A) — entries for one category.
 *   GET/POST/PUT/DELETE /v1/categories/{key}/entries[/{id}]
 *
 * Single-tier super_admin/trustee (no org-admin surface — AC3 is in BIOT-k):
 *   - Stock curation:   scope:"stock"               (trustee/super_admin)
 *   - On-behalf-of org: scope:"org" + admin_org_id  (super_admin picks the org)
 *
 * `properties` is edited as validated JSON and checked server-side against the
 * category's property_schema; a 422 is surfaced cleanly.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createEntry,
  deleteEntry,
  getCategory,
  listEntries,
  updateEntry,
  type CategoryOut,
  type EntryOut,
  type EntryScope,
} from "../auth/mastersApi";
import { listOrganizations, type OrganizationSummary } from "../auth/adminApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";
import { JsonField, parseJsonObject } from "../components/JsonField";

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function scopeOf(e: EntryOut): "stock" | "org" {
  return e.organization_id === null ? "stock" : "org";
}

function csvToList(s: string): string[] | null {
  const arr = s.split(",").map((x) => x.trim()).filter(Boolean);
  return arr.length ? arr : null;
}

export function CatalogEntriesPage(): ReactNode {
  const { key = "" } = useParams();
  const category = useAsync<CategoryOut>(() => getCategory(key), [key]);
  const entries = useAsync<EntryOut[]>(() => listEntries(key), [key]);
  // Orgs for the admin_org_id picker (reuse the oxbo-auth B7 admin endpoint).
  const orgs = useAsync(() => listOrganizations({ limit: 200 }), []);

  const [term, setTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EntryOut | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const list = entries.data ?? [];
    const q = term.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (e) =>
        e.entry_key.toLowerCase().includes(q) || e.display_label.toLowerCase().includes(q),
    );
  }, [entries.data, term]);

  async function onDelete(e: EntryOut) {
    if (!window.confirm(`Delete entry "${e.entry_key}"?`)) return;
    setMutationError(null);
    try {
      await deleteEntry(key, e.id);
      entries.reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to={`/catalog/${encodeURIComponent(key)}`} className="text-sm text-oxbo hover:underline">
          ← {category.data?.display_name ?? key}
        </Link>
      </div>
      <PageHeader title={`Entries — ${category.data?.display_name ?? key}`}>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setShowForm((s) => !s);
          }}
          className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
        >
          {showForm && !editing ? "Close" : "New entry"}
        </button>
      </PageHeader>

      {mutationError && <ErrorBanner message={mutationError} />}

      {(showForm || editing) && (
        <EntryForm
          categoryKey={key}
          orgs={orgs.data?.organizations ?? []}
          editing={editing}
          onDone={() => {
            setShowForm(false);
            setEditing(null);
            entries.reload();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onError={setMutationError}
        />
      )}

      <input
        type="search"
        placeholder="Filter by key or label…"
        aria-label="Filter entries"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        className="w-72 rounded border border-slate-300 px-3 py-1.5 text-sm"
      />

      {entries.loading ? (
        <Spinner />
      ) : entries.error ? (
        <ErrorBanner message={errorMessage(entries.error)} />
      ) : filtered.length === 0 ? (
        <EmptyState message={term ? "No entries match." : "No entries in this category."} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Label</th>
                <th className="px-3 py-2 font-medium">Scope</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium">Modified</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{e.entry_key}</td>
                  <td className="px-3 py-2">{e.display_label}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={scopeOf(e)} />
                    {e.organization_id && (
                      <span className="ml-1 font-mono text-[10px] text-slate-400">
                        {e.organization_id.slice(0, 8)}…
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{e.is_active ? "yes" : "no"}</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(e.modified_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditing(e);
                        }}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(e)}
                        className="rounded border border-slate-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EntryForm({
  categoryKey,
  orgs,
  editing,
  onDone,
  onCancel,
  onError,
}: {
  categoryKey: string;
  orgs: OrganizationSummary[];
  editing: EntryOut | null;
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}): ReactNode {
  const isEdit = editing !== null;
  const [scope, setScope] = useState<EntryScope>("stock");
  const [adminOrgId, setAdminOrgId] = useState("");
  const [entryKey, setEntryKey] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [siteRegion, setSiteRegion] = useState("");
  const [aliases, setAliases] = useState("");
  const [propsRaw, setPropsRaw] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setEntryKey(editing.entry_key);
      setDisplayLabel(editing.display_label);
      setSiteRegion(editing.site_region_key ?? "");
      setAliases((editing.aliases ?? []).join(", "));
      setPropsRaw(editing.properties ? JSON.stringify(editing.properties, null, 2) : "");
      setIsActive(editing.is_active);
      setScope(scopeOf(editing));
      setAdminOrgId(editing.organization_id ?? "");
    }
  }, [editing]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    onError("");
    const props = parseJsonObject(propsRaw);
    if (!props.ok) {
      onError("properties is not valid JSON.");
      return;
    }
    if (!isEdit && scope === "org" && !adminOrgId) {
      onError("Select the organization this org-scoped entry belongs to.");
      return;
    }
    setBusy(true);
    try {
      if (isEdit && editing) {
        // EntryUpdate: scope/org are immutable on the API.
        await updateEntry(categoryKey, editing.id, {
          display_label: displayLabel.trim(),
          aliases: csvToList(aliases),
          properties: props.value,
          is_active: isActive,
          site_region_key: siteRegion.trim() || null,
        });
      } else {
        await createEntry(categoryKey, {
          entry_key: entryKey.trim(),
          display_label: displayLabel.trim(),
          scope,
          is_active: isActive,
          aliases: csvToList(aliases),
          properties: props.value,
          site_region_key: siteRegion.trim() || null,
          ...(scope === "org" ? { admin_org_id: adminOrgId } : {}),
        });
      }
      onDone();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-700">{isEdit ? "Edit entry" : "New entry"}</div>

      {!isEdit && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Scope</span>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as EntryScope)}
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            >
              <option value="stock">stock (shared catalog)</option>
              <option value="org">org (on behalf of an organization)</option>
            </select>
          </label>
          {scope === "org" && (
            <label className="text-sm">
              <span className="mb-1 block text-slate-600">Organization (admin_org_id)</span>
              <select
                value={adminOrgId}
                onChange={(e) => setAdminOrgId(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1.5"
              >
                <option value="">— select organization —</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name ?? o.id}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Entry key</span>
          <input
            required
            disabled={isEdit}
            value={entryKey}
            onChange={(e) => setEntryKey(e.target.value)}
            placeholder="wbm"
            className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs disabled:bg-slate-100"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Display label</span>
          <input
            required
            value={displayLabel}
            onChange={(e) => setDisplayLabel(e.target.value)}
            placeholder="Water-Based Mud"
            className="w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Site/region key (optional)</span>
          <input
            value={siteRegion}
            onChange={(e) => setSiteRegion(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Aliases (comma-separated)</span>
          <input
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </label>
      </div>

      <JsonField label="properties (JSON, validated against the category schema)" value={propsRaw} onChange={setPropsRaw} />

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-oxbo px-4 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark disabled:opacity-50"
        >
          {busy ? "Saving…" : isEdit ? "Save entry" : "Create entry"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
